/**
 * Google Analytics (GA4) — inicialização e rastreamento de eventos.
 *
 * O ID de medição vem do conector do Google Analytics
 * (VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY) ou, alternativamente,
 * de VITE_GA_MEASUREMENT_ID. Sem ID configurado, todas as funções são no-op.
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const measurementId =
  (import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY as string | undefined) ||
  (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined) ||
  "";

let initialized = false;

/**
 * O gtag exige que o objeto `arguments` seja empurrado no dataLayer
 * (um Array comum não é processado pelo GA4).
 */
const gtag = function (...args: unknown[]) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
} as (...args: unknown[]) => void;


export const isAnalyticsEnabled = () => Boolean(measurementId);

export const initAnalytics = () => {
  if (initialized || !measurementId || typeof window === "undefined") return;
  initialized = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = gtag;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);



  gtag("js", new Date());
  // page_view manual: o app é SPA e o tracker de rota envia cada visualização
  gtag("config", measurementId, { send_page_view: false });

  initAutoClickTracking();
};

export const trackPageView = (path: string, title?: string) => {
  if (!measurementId) return;
  gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: title ?? document.title,
  });
};

export const trackEvent = (name: string, params: Record<string, unknown> = {}) => {
  if (!measurementId) return;
  gtag("event", name, params);
};

/** Nomes de eventos padronizados usados pelo site. */
export const AnalyticsEvents = {
  ctaSejaParte: "cta_seja_parte",
  ctaSejaParceiro: "cta_seja_parceiro",
  ctaProcessoSeletivo: "cta_processo_seletivo",
  processoSeletivoInteresse: "processo_seletivo_interesse",
  socialClick: "social_click",
  outboundClick: "outbound_click",
  emailClick: "email_click",
  vanguardDownload: "vanguard_carta_download",
  contactFormSubmit: "contact_form_submit",
  eventRegistration: "event_registration",
} as const;

const socialFromUrl = (url: string): string | null => {
  const host = (() => {
    try {
      return new URL(url).hostname.replace(/^www\./, "").toLowerCase();
    } catch {
      return "";
    }
  })();
  if (host.includes("instagram")) return "instagram";
  if (host.includes("linkedin")) return "linkedin";
  if (host.includes("youtube") || host.includes("youtu.be")) return "youtube";
  if (host.includes("whatsapp") || host.includes("wa.me")) return "whatsapp";
  if (host.includes("tiktok")) return "tiktok";
  if (host.includes("facebook")) return "facebook";
  if (host.includes("x.com") || host.includes("twitter")) return "twitter";
  return null;
};

/**
 * Rastreia automaticamente cliques em links externos, redes sociais e e-mails,
 * em qualquer página, sem precisar instrumentar cada link individualmente.
 */
export const initAutoClickTracking = () => {
  if (typeof document === "undefined") return;

  document.addEventListener(
    "click",
    (event) => {
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest?.("a") as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = anchor.getAttribute("href") || "";
      if (!href || href.startsWith("#")) return;

      const label = (anchor.getAttribute("aria-label") || anchor.textContent || "").trim().slice(0, 80);

      if (href.startsWith("mailto:")) {
        trackEvent(AnalyticsEvents.emailClick, {
          email: href.replace("mailto:", "").split("?")[0],
          label,
          page_path: window.location.pathname,
        });
        return;
      }

      if (!/^https?:\/\//i.test(href)) return;

      let isExternal = true;
      try {
        isExternal = new URL(href).hostname !== window.location.hostname;
      } catch {
        isExternal = true;
      }
      if (!isExternal) return;

      const network = socialFromUrl(href);
      if (network) {
        trackEvent(AnalyticsEvents.socialClick, {
          network,
          url: href,
          label,
          page_path: window.location.pathname,
        });
        return;
      }

      trackEvent(AnalyticsEvents.outboundClick, {
        url: href,
        label,
        page_path: window.location.pathname,
      });
    },
    { capture: true },
  );
};
